import { Component, Input, OnInit } from '@angular/core';
import { DataAnalysisApiService } from '../../services/api/data-analysis-api.service';
import { UtilService } from '../../../../core/services/util/util.service';
import { PlatformService } from '../../../../core/services/platform/platform.service';
import { FormBuilder, FormGroup } from '@angular/forms';
import { faCaretDown, faCaretUp, faSort, faXmark } from '@fortawesome/free-solid-svg-icons';
import { Color, ScaleType } from '@swimlane/ngx-charts';

interface ChartDataset {
  name: string;
  series: Series[]
}
interface Series {
  name: string;
  value: number;
}

interface TableDataset {
  sku: string;
  name: string;
  stock: number;
  channels: ChannelSummary[],
  summary_qty: number;
  summary_amount: number;
}
interface ChannelSummary {
  channel: string;
  total_amount: number;
  total_qty: number;
}

@Component({
  selector: 'app-data-analysis-product-overview',
  templateUrl: './data-analysis-product-overview.component.html',
  styleUrl: './data-analysis-product-overview.component.css'
})
export class DataAnalysisProductOverviewComponent implements OnInit {

  @Input() branchMode = false;

  faCaretDown = faCaretDown;
  faCaretUp = faCaretUp;
  faSort = faSort;
  faXmark = faXmark

  chartDatasets: ChartDataset[] = [];
  tableDatasets: TableDataset[] = []

  channels: any[] = [];
  channelFiltered: any[] = [];

  products: Array<{ sku: string; name: string, in_stock: number }> = [];
  keyword: string = '';
  productsFilter: Array<{ sku: string; name: string }> = [];
  showProductFilter = false;
  productSelected: Array<{ sku: string; name: string }> = [];

  productFiltered: Array<{ sku: string; total_qty: number; total_amount: number }> = []
  productSummary: Array<{ channel: string; sku: string; total_qty: number; total_amount: number }> = []

  summaryAmount: number = 0;
  summaryQty: number = 0;

  displayData: string = 'total_qty';
  channelSelected: string[] = [];
  readonly ALL_CHANNEL_MARKER = '__all__';

  selectedDate: { type: string, value: string } = { type: 'daypass', value: 'month' };

  readonly chartTopN = 5;
  readonly chartColors = ['#647c8a', '#3f51b5', '#2196f3', '#00b862', '#afdf0a', '#a7b61a', '#f3e562', '#ff9800', '#ff5722', '#ff9907'];
  hiddenChannels = new Set<string>();
  chartScheme: Color = { name: 'channel', selectable: true, group: ScaleType.Ordinal, domain: [] };
  readonly TOTAL_ROW_NAME = 'รวม';

  form: FormGroup;
  loading: boolean = false;

  sortSelected: string = '';
  sortBy: string = 'max';

  constructor(
    private dataAnalysisApiService: DataAnalysisApiService,
    private utilService: UtilService,
    private platformService: PlatformService,
    private fb: FormBuilder,
  ) {
    this.form = this.fb.group({
      date: this.computeDateRange(this.selectedDate),
      sku: [''],
      channel: ['']
    });

  }

  handleOptionSelected(selectedDate: { type: string, value: string }) {
    this.selectedDate = selectedDate;
    this.form.get('date')?.setValue(this.computeDateRange(selectedDate));
  }

  private computeDateRange(option: { type: string, value: string }): string {
    const dayjs = this.utilService.dayjs;
    if (option.type === 'date-range') {
      const [s, e] = option.value.split(',');
      return `${dayjs(s).format('YYYY-MM-DD')},${dayjs(e).format('YYYY-MM-DD')}`;
    }
    if (option.value === '0') {
      const d = dayjs().format('YYYY-MM-DD');
      return `${d},${d}`;
    }
    if (option.value === '1') {
      const d = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
      return `${d},${d}`;
    }
    if (option.value === 'month') {
      const start = dayjs().startOf('month').format('YYYY-MM-DD');
      const end = dayjs().format('YYYY-MM-DD');
      return `${start},${end}`;
    }
    const start = dayjs().subtract(parseInt(option.value), 'day').format('YYYY-MM-DD');
    const end = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
    return `${start},${end}`;
  }

  ngOnInit(): void {
    const all = this.platformService?.channels || [];
    this.channels = all.filter(c => !!c.is_branch === this.branchMode);
    this.channelFiltered = this.channels;
    this.getDataReport();
  }

  async getDataReport() {
    if (this.loading) return;
    this.loading = true;
    const value = this.form.getRawValue()

    let filter: any = { limit: 10, sort_field: this.displayData, sort_by: this.sortBy }
    if (value.date) {
      filter['date'] = value.date
    }
    if (this.channelSelected.length > 0) {
      filter['field_channel'] = this.channelSelected.join()
      this.channelFiltered = this.channels.filter(c => this.channelSelected.includes(c.shop_ref))
    } else {
      filter['field_channel'] = this.channels.map((item) => item.shop_ref).toString()
      this.channelFiltered = this.channels
    }
    if (this.productSelected.length > 0) {
      filter['sku'] = this.productSelected.map(e => e.sku).join()
    }
    if (this.sortSelected) {
      filter['channel'] = this.sortSelected
    }

    this.productFiltered = [];
    this.productSummary = [];

    try {
      const res: any = await this.dataAnalysisApiService.getProductSummaryReport(filter);
      if (res.success) {
        this.productFiltered = res.data.products;
        this.productSummary = res.data.summary;
        const uniqueSku = [...new Set(res.data.products.map((e: any) => e.sku))];
        if (uniqueSku.length > 0) {
          await this.findAllProduct({ sku: uniqueSku.join(), filed: 'sku,name' });
        }
      }
      this.setFormatingData()
      this.calculateStatistic()
    } catch {
      this.productFiltered = [];
      this.productSummary = [];
      this.tableDatasets = [];
      this.chartDatasets = [];
      this.summaryAmount = 0;
      this.summaryQty = 0;
    } finally {
      this.loading = false;
    }
  }

  async searchProduct(keyword: string) {
    return await this.dataAnalysisApiService.searchProduct({ keyword, limit: 20 }).then((res: any) => {
      if (res.success) {
        return res.data;
      }
      return []
    })
  }

  async findAllProduct(filter: any) {
    await this.dataAnalysisApiService.getAllProduct(filter).then((res: any) => {
      if (res.success) {
        this.products = res.data;
      } else {
        this.products = []
      }
    })
    if (this.branchMode && this.products.length > 0) {
      await this.applyBranchStock();
    }
  }

  breakdownLoading: boolean = false;
  breakdownItems: Array<{ shop_ref: string; platform_name: string; color?: string; sellable: number }> = [];

  async loadStockBreakdown(sku: string) {
    this.breakdownLoading = true;
    this.breakdownItems = [];
    const channel = this.channelFiltered.map(c => c.shop_ref);
    if (!sku || channel.length === 0) {
      this.breakdownLoading = false;
      return;
    }
    try {
      const res: any = await this.dataAnalysisApiService.getStockDetail({ sku, channel });
      if (res?.success) {
        const stockMap = new Map<string, number>(
          (res.data || []).map((e: any) => [e.channel, e.sellable])
        );
        this.breakdownItems = this.channelFiltered.map(c => ({
          shop_ref: c.shop_ref,
          platform_name: c.platform_name,
          color: c.color,
          sellable: stockMap.get(c.shop_ref) ?? 0,
        })).sort((a, b) => b.sellable - a.sellable);
      }
    } catch {
      this.breakdownItems = [];
    } finally {
      this.breakdownLoading = false;
    }
  }

  private async applyBranchStock() {
    const sku = this.products.map(p => p.sku);
    const channel = this.channelFiltered.map(c => c.shop_ref);
    if (sku.length === 0 || channel.length === 0) return;
    try {
      const res: any = await this.dataAnalysisApiService.getStockBySku({ sku, channel });
      if (res?.success) {
        const stockMap = new Map<string, number>(
          (res.data || []).map((e: any) => [e.sku, e.sellable])
        );
        this.products = this.products.map(p => ({ ...p, in_stock: stockMap.get(p.sku) ?? 0 }));
      }
    } catch { }
  }

  setFormatingData() {
    this.rebuildChartScheme();
    this.chartDatasets = this.formatGraphData();
    this.tableDatasets = []

    for (const product of this.productFiltered) {
      const channels = []
      for (const channel of this.channelFiltered) {
        const res = this.productSummary.find(e => e.channel === channel.shop_ref && e.sku === product.sku)
        channels.push({
          channel: channel.shop_ref,
          total_amount: res?.total_amount || 0,
          total_qty: res?.total_qty || 0
        })
      }
      this.tableDatasets.push({
        sku: product.sku,
        name: this.products.find(p => p.sku === product.sku)?.name || '',
        stock: this.products.find(p => p.sku === product.sku)?.in_stock || 0,
        channels: channels,
        summary_qty: product.total_qty,
        summary_amount: product.total_amount
      })
    }

    this.tableDatasets.push({
      name: this.TOTAL_ROW_NAME,
      sku: '',
      stock: 0,
      channels: this.channelFiltered.map(e => ({
        channel: e.shop_ref,
        total_amount: this.productSummary.filter(p => p.channel === e.shop_ref).reduce((n, { total_amount }) => n + total_amount, 0),
        total_qty: this.productSummary.filter(p => p.channel === e.shop_ref).reduce((n, { total_qty }) => n + total_qty, 0)
      })),
      summary_amount: this.productFiltered.reduce((n, { total_amount }) => n + total_amount, 0),
      summary_qty: this.productFiltered.reduce((n, { total_qty }) => n + total_qty, 0)
    })
  }

  private formatGraphData(): ChartDataset[] {
    const metric: 'total_qty' | 'total_amount' = this.displayData === 'total_qty' ? 'total_qty' : 'total_amount';
    const sortedSkus = [...this.productFiltered]
      .sort((a, b) => (b[metric] || 0) - (a[metric] || 0))
      .slice(0, this.chartTopN)
      .map(p => p.sku);

    return sortedSkus.map(sku => {
      const productName = this.products.find(p => p.sku === sku)?.name || sku;
      const series: Series[] = this.channelFiltered.map(channel => {
        const summary = this.productSummary.find(item => item.sku === sku && item.channel === channel.shop_ref);
        const isHidden = this.hiddenChannels.has(channel.shop_ref);
        return {
          name: channel.platform_name,
          value: isHidden ? 0 : (summary?.[metric] || 0)
        };
      });
      return { name: productName, series };
    });
  }

  private rebuildChartScheme(): void {
    this.chartScheme = {
      name: 'channel',
      selectable: true,
      group: ScaleType.Ordinal,
      domain: this.channelFiltered.map((c, i) => c.color || this.chartColors[i % this.chartColors.length])
    };
  }

  toggleChannelVisibility(shopRef: string): void {
    if (this.hiddenChannels.has(shopRef)) {
      this.hiddenChannels.delete(shopRef);
    } else {
      this.hiddenChannels.add(shopRef);
    }
    this.chartDatasets = this.formatGraphData();
  }

  channelColor(index: number): string {
    const channel = this.channelFiltered[index];
    return channel?.color || this.chartColors[index % this.chartColors.length];
  }

  calculateStatistic() {
    this.summaryAmount = this.productFiltered.reduce((n, { total_amount }) => n + total_amount, 0)
    this.summaryQty = this.productFiltered.reduce((n, { total_qty }) => n + total_qty, 0)
  }

  get displayedChannelSelection(): string[] {
    if (this.channels.length > 0 && this.channelSelected.length === this.channels.length) {
      return [this.ALL_CHANNEL_MARKER, ...this.channelSelected];
    }
    return this.channelSelected;
  }

  onChannelSelectionChange(values: any[]) {
    const list = Array.isArray(values) ? values : [];
    const wasAllSelected = this.channels.length > 0
      && this.channelSelected.length === this.channels.length;
    const newHasMarker = list.includes(this.ALL_CHANNEL_MARKER);

    if (newHasMarker !== wasAllSelected) {
      this.channelSelected = newHasMarker
        ? this.channels.map(c => c.shop_ref)
        : [];
    } else {
      this.channelSelected = list.filter((v: unknown): v is string =>
        typeof v === 'string' && v !== this.ALL_CHANNEL_MARKER
      );
    }
  }

  removeChannelSelected(channel: any) {
    this.channelSelected = this.channelSelected.filter((e: string) => e !== channel.shop_ref)
  }

  removeChannelSelectedAll() {
    this.channelSelected = []
  }

  displaychannel() {
    return this.channels.filter(c => this.channelSelected.includes(c.shop_ref))
  }

  displayDataOnchange(event: any) {
    this.displayData = event.value
    this.getDataReport()
  }

async productSearch(dataSearch: any) {
    if (this.keyword.length > 2) {
      try {
        const uniquesku = this.productSelected.map(e => e.sku);
        const results = await this.searchProduct(this.keyword);
        this.productsFilter = (results || []).filter((e: any) => !uniquesku.includes(e.sku))
        this.showProductFilter = true;
      } catch {
        this.productsFilter = [];
        this.showProductFilter = false;
      }
    } else {
      this.showProductFilter = false;
    }
  }

  selectProduct(product: any) {
    this.productSelected.push(product)
    this.productsFilter = this.productsFilter.filter((e: any) => e.sku !== product.sku)
    if (this.productsFilter.length === 0) {
      this.showProductFilter = false;
    }
  }

  removeProductSelected(product: any) {
    this.productSelected = this.productSelected.filter(e => e.sku !== product.sku)
  }

  removeProductSelectedAll() {
    this.productSelected = []
  }

  submitForm() {
    this.sortSelected = '';
    this.sortBy = 'max'
    this.getDataReport();
  }

  sortData(shop_ref: string) {

    if (this.sortSelected !== shop_ref) {
      this.sortSelected = shop_ref;
      this.sortBy = 'max'
    } else {
      if (!this.sortBy) this.sortBy = 'max'
      else if (this.sortBy === 'max') this.sortBy = 'min'
      else if (this.sortBy === 'min') this.sortBy = 'max'
    }

    // call api
    this.getDataReport();
  }

  clearSearch() {
    this.keyword = ''
    this.showProductFilter = false
  }

}